/**
 * Billing API
 *
 * Handles all billing-related API calls.
 * Uses Zod validation for all endpoints (financial data is HIGH RISK).
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import {
  getBudgetResponseSchema,
  balanceResponseSchema,
  ledgerResponseSchema,
  perMinuteBillingSummaryResponseSchema,
  getPricingPlanResponseSchema,
  marginReportResponseSchema,
  autoRechargeEnvelopeSchema,
  autoRechargeSetupResponseSchema,
} from '@aica/api-types/schemas';
import type {
  AutoRechargeEnvelope,
  AutoRechargeSetupResponse,
  UpdateAutoRechargeRequest,
} from '@aica/api-types/schemas';
import {
  checkoutResponseSchema,
  portalResponseSchema,
  subscriptionResponseSchema,
  cancelSubscriptionResponseSchema,
  reactivateSubscriptionResponseSchema,
  changeSubscriptionResponseSchema,
  previewSubscriptionChangeResponseSchema,
} from '@aica/api-types/schemas/stripe';
import type {
  CheckoutResponse,
  PortalResponse,
  SubscriptionResponse,
  CancelSubscriptionRequest,
  CancelSubscriptionResponse,
  ReactivateSubscriptionResponse,
  ChangeSubscriptionRequest,
  ChangeSubscriptionResponse,
  PreviewSubscriptionChangeRequest,
  PreviewSubscriptionChangeResponse,
} from '@aica/api-types/schemas/stripe';
import type {
  BillingBudgetResponse,
  BillingBalanceResponse,
  BillingLedgerResponse,
  SuccessResponse,
} from '../types';
import type {
  BillingSummaryResponse,
  PricingPlanApiResponse,
  MarginReportApiResponse,
} from '../types/billing.types';
interface TierPricingResponse {
  tier: string;
  ratePerMinute: number;
  ratePerChat: number;
  currency: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

function buildQueryString(
  params?: Record<string, string | number | boolean | undefined>
): string {
  if (!params) return '';
  const filtered = Object.fromEntries(
    Object.entries(params)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, String(v)])
  );
  const qs = new URLSearchParams(filtered).toString();
  return qs ? `?${qs}` : '';
}

export class BillingApi extends BaseApiClient {
  /**
   * Get billing summary (per-minute model)
   * Returns balance, usage (minutes/chats/charges), and budget status
   */
  async getBillingSummary(params?: {
    periodStart?: string;
    periodEnd?: string;
    /**
     * Считать ли разбивку минут по котлам. Просит её только экран биллинга —
     * остальным потребителям сводки она стоила бы лишнего скана
     * `usage_charges` на каждой загрузке.
     */
    includeMinuteSplit?: boolean;
  }): Promise<BillingSummaryResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.BILLING_SUMMARY}${buildQueryString(params)}`,
      perMinuteBillingSummaryResponseSchema
    );
  }

  /**
   * Get billing budget - validated with Zod
   */
  async getBillingBudget(orgId: string): Promise<BillingBudgetResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.BILLING_BUDGET(orgId),
      getBudgetResponseSchema
    );
  }

  /**
   * Get billing balance - validated with Zod
   */
  async getBillingBalance(orgId: string): Promise<BillingBalanceResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.BILLING_BALANCE(orgId),
      balanceResponseSchema
    );
  }

  /**
   * Get billing ledger - validated with Zod
   */
  async getBillingLedger(
    orgId: string,
    params?: {
      periodStart?: string;
      periodEnd?: string;
      entryType?: string;
      /** Размер страницы. Без него сервер отдаёт свои 50 — молча. */
      limit?: number;
      /**
       * Смещение страницы. Экран биллинга им НЕ листает — см. курсор ниже;
       * остаётся для прямых вызовов API.
       */
      offset?: number;
      /**
       * Курсор: продолжить после строки с этой меткой и этим id.
       *
       * Пара, а не одна метка: `created_at` не уникален, и у записей одной
       * транзакции он совпадает. Смещение здесь не годится вовсе — журнал
       * живой, и вставка между страницами сдвигает окно, задваивая строку на
       * границе и теряя соседнюю.
       */
      cursorCreatedAt?: string;
      cursorId?: string;
    }
  ): Promise<BillingLedgerResponse> {
    return this.validatedRequest(
      `${API_ENDPOINTS.BILLING_LEDGER(orgId)}${buildQueryString(params)}`,
      ledgerResponseSchema
    );
  }

  /**
   * Add credits - POST request (no validation needed for response)
   */
  async addCredits(data: {
    amount: number;
    source: string;
    description: string;
  }): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.BILLING_CREDITS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update billing budget - PUT request
   */
  async updateBillingBudget(
    orgId: string,
    data: {
      monthlyLimitCents?: number;
      notifyThresholdPercent?: number;
      notifyEmails?: string[];
    }
  ): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.BILLING_BUDGET(orgId), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get pricing plan for org
   */
  async getPricingPlan(orgId: string): Promise<PricingPlanApiResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.BILLING_PRICING(orgId),
      getPricingPlanResponseSchema
    );
  }

  /**
   * Update pricing plan (platform admin only)
   */
  async updatePricingPlan(
    orgId: string,
    data: {
      planName?: string;
      ratePerMinute: number;
      ratePerChat?: number;
      effectiveFrom?: string;
      tier?: string;
    }
  ): Promise<PricingPlanApiResponse> {
    return this.request<PricingPlanApiResponse>(API_ENDPOINTS.BILLING_PRICING(orgId), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Remove a per-org pricing override (platform admin only).
   * Reverts the organization to its tier default.
   */
  async removePricingOverride(
    orgId: string
  ): Promise<{ success: boolean; deactivated: number }> {
    return this.request(`${API_ENDPOINTS.BILLING_PRICING(orgId)}/override`, {
      method: 'DELETE',
    });
  }

  /**
   * Get margin report (platform admin only)
   */
  async getMarginReport(
    orgId: string,
    params: { periodStart: string; periodEnd: string }
  ): Promise<MarginReportApiResponse> {
    const query = new URLSearchParams(params).toString();
    return this.validatedRequest(
      `${API_ENDPOINTS.BILLING_MARGIN(orgId)}?${query}`,
      marginReportResponseSchema
    );
  }

  /**
   * Create Stripe Checkout session for subscription upgrade
   */
  async createCheckoutSession(data: {
    tier: string;
    successUrl: string;
    cancelUrl: string;
  }): Promise<CheckoutResponse> {
    return this.validatedRequest(API_ENDPOINTS.STRIPE_CHECKOUT, checkoutResponseSchema, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Create Stripe Customer Portal session
   */
  async createPortalSession(data: { returnUrl: string }): Promise<PortalResponse> {
    return this.validatedRequest(API_ENDPOINTS.STRIPE_PORTAL, portalResponseSchema, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Create Stripe Checkout session for credit purchase
   */
  async createCreditPurchase(data: {
    amountCents: number;
    successUrl: string;
    cancelUrl: string;
  }): Promise<CheckoutResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.STRIPE_CREDIT_PURCHASE,
      checkoutResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Get auto-recharge config + saved-card view for an org
   */
  async getAutoRecharge(orgId: string): Promise<AutoRechargeEnvelope> {
    return this.validatedRequest(
      API_ENDPOINTS.BILLING_AUTO_RECHARGE(orgId),
      autoRechargeEnvelopeSchema
    );
  }

  /**
   * Update auto-recharge config (enabled / threshold / amount)
   */
  async updateAutoRecharge(
    orgId: string,
    data: UpdateAutoRechargeRequest
  ): Promise<AutoRechargeEnvelope> {
    return this.validatedRequest(
      API_ENDPOINTS.BILLING_AUTO_RECHARGE(orgId),
      autoRechargeEnvelopeSchema,
      { method: 'PUT', body: JSON.stringify(data) }
    );
  }

  /**
   * Start card collection — returns a hosted setup-mode Checkout URL
   */
  async createAutoRechargeSetup(
    orgId: string,
    data: { successUrl: string; cancelUrl: string }
  ): Promise<AutoRechargeSetupResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.BILLING_AUTO_RECHARGE_SETUP(orgId),
      autoRechargeSetupResponseSchema,
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  /**
   * Get tier pricing (platform admin only)
   */
  async getTierPricing(): Promise<{ success: boolean; tiers: TierPricingResponse[] }> {
    return this.request('/api/v1/admin/tier-pricing');
  }

  /**
   * Update tier pricing (platform admin only)
   */
  async updateTierPricing(
    tier: string,
    data: { ratePerMinute: number; ratePerChat: number }
  ): Promise<{ success: boolean; tier: TierPricingResponse }> {
    return this.request(`/api/v1/admin/tier-pricing/${tier}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get current Stripe subscription details
   */
  async getSubscription(): Promise<SubscriptionResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.STRIPE_SUBSCRIPTION,
      subscriptionResponseSchema
    );
  }

  /**
   * Cancel subscription at the end of the current billing period.
   *
   * `data` необязателен: причина спрашивается только в диалоге отмены, а тот же
   * метод вызывается и без неё.
   */
  async cancelSubscription(
    data?: CancelSubscriptionRequest
  ): Promise<CancelSubscriptionResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.STRIPE_SUBSCRIPTION_CANCEL,
      cancelSubscriptionResponseSchema,
      { method: 'POST', body: JSON.stringify(data ?? {}) }
    );
  }

  /**
   * Undo a scheduled cancellation (reactivate subscription)
   */
  async reactivateSubscription(): Promise<ReactivateSubscriptionResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.STRIPE_SUBSCRIPTION_REACTIVATE,
      reactivateSubscriptionResponseSchema,
      { method: 'POST' }
    );
  }

  /**
   * Change subscription plan in-app.
   * Upgrades apply immediately (prorated, effectiveAt: 'now'); downgrades are
   * scheduled for the end of the current period (effectiveAt: 'period_end') so
   * the current plan and its prepaid minutes are kept until then.
   */
  async changeSubscription(
    data: ChangeSubscriptionRequest
  ): Promise<ChangeSubscriptionResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.STRIPE_SUBSCRIPTION_CHANGE,
      changeSubscriptionResponseSchema,
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  /**
   * What an upgrade costs today, priced by Stripe.
   *
   * Upgrades only — a downgrade takes no money now and the endpoint rejects it.
   */
  async previewSubscriptionChange(
    data: PreviewSubscriptionChangeRequest
  ): Promise<PreviewSubscriptionChangeResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.STRIPE_SUBSCRIPTION_CHANGE_PREVIEW,
      previewSubscriptionChangeResponseSchema,
      { method: 'POST', body: JSON.stringify(data) }
    );
  }
}
